import { DatePipe, DOCUMENT, NgClass } from '@angular/common';
import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, Output, EventEmitter, inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastService } from 'src/app/services/toast.service';
import {
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
} from 'date-fns';
import { ScheduleDatesFilterModel } from 'src/app/models/filter-date.model';
import { MatFormField, MatLabel, MatHint, MatSuffix } from '@angular/material/input';
import { MatDateRangeInput, MatStartDate, MatEndDate, MatDatepickerToggle, MatDatepickerToggleIcon, MatDateRangePicker } from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'app-dates-filter',
    templateUrl: './dates-filter.component.html',
    styleUrls: ['./dates-filter.component.scss'],
    imports: [NgClass, MatFormField, MatLabel, MatDateRangeInput, FormsModule, ReactiveFormsModule, MatStartDate, MatEndDate, MatHint, MatDatepickerToggle, MatSuffix, MatIcon, MatDatepickerToggleIcon, MatDateRangePicker]
})
export class DatesFilterComponent implements OnInit, OnDestroy {
  toastr = inject(ToastService);
  datepipe = inject(DatePipe);
  private readonly elementRef = inject(ElementRef);
  private readonly document = inject(DOCUMENT);
  // Whether the filter was already visible when the current click started.
  // The click that opens the filter (from any trigger the host renders) must
  // not be treated as a click outside, or it would close it right away.
  private visibleAtClickStart = false;
  private readonly recordVisibilityAtClickStart = () => {
    this.visibleAtClickStart = this.visibility;
  };

  constructor() {
    // Capture phase: runs before the trigger's own (click) handler toggles visibility.
    this.document.addEventListener('click', this.recordVisibilityAtClickStart, true);
  }


  dateForm = this.createReport();
  formatFrom = 'yyyy-MM-ddT00:00:00';
  formatTo = 'yyyy-MM-ddT23:59:59';
  dates: ScheduleDatesFilterModel = {
    from: '',
    to: '',
  };

  @Output() sendDates = new EventEmitter<ScheduleDatesFilterModel>();
  @Output() clickOutside = new EventEmitter<void>();
  @Input() visibility: boolean = false;
  @Input() isPopover: boolean = true;
  @Input() showApplyBtn: boolean = true;
  @Input() autoApply: boolean = false;
  @Input() defautFromDate: Date | null = null;
  @Input() defautToDate: Date | null = null;
  @Input() showSidePanel: boolean = true;
  isVisible: boolean = false;
  selectedOption: string = '';

  createReport(): UntypedFormGroup {
    return new UntypedFormGroup({
      fromDate: new UntypedFormControl(this.defautFromDate),
      toDate: new UntypedFormControl(this.defautToDate),
    });
  }

  ngOnInit(): void {
    this.setPicker(this.defautFromDate, this.defautToDate);
  }

  ngOnDestroy(): void {
    this.document.removeEventListener('click', this.recordVisibilityAtClickStart, true);
  }

  setPicker(from: any, to: any) {
    this.dateForm.controls.fromDate.patchValue(from);
    this.dateForm.controls.toDate.patchValue(to);
  }

  resetForm() {
    this.dateForm.reset();
  }

  setToday() {
    const today = new Date();
    this.setDateRange(today, today);
    this.selectedOption = 'today';
    this.dateForm.controls.fromDate.patchValue(today);
    this.dateForm.controls.toDate.patchValue(today);
  }

  setThisWeek() {
    const startOfWeekDate = startOfWeek(new Date(), { weekStartsOn: 1 });
    const endOfWeekDate = endOfWeek(new Date(), { weekStartsOn: 1 });
    this.setDateRange(startOfWeekDate, endOfWeekDate);
    this.selectedOption = 'thisWeek';
    this.dateForm.controls.fromDate.patchValue(startOfWeekDate);
    this.dateForm.controls.toDate.patchValue(endOfWeekDate);
  }

  setThisMonth() {
    const startOfMonthDate = startOfMonth(new Date());
    const endOfMonthDate = endOfMonth(new Date());
    this.setDateRange(startOfMonthDate, endOfMonthDate);
    this.selectedOption = 'thisMonth';
    this.dateForm.controls.fromDate.patchValue(startOfMonthDate);
    this.dateForm.controls.toDate.patchValue(endOfMonthDate);
  }

  setThisYear() {
    const startOfYearDate = startOfYear(new Date());
    const endOfYearDate = endOfYear(new Date());
    this.setDateRange(startOfYearDate, endOfYearDate);
    this.selectedOption = 'thisYear';
    this.dateForm.controls.fromDate.patchValue(startOfYearDate);
    this.dateForm.controls.toDate.patchValue(endOfYearDate);
  }

  setDateRange(from: Date, to: Date) {
    const tempFrom = this.datepipe.transform(from, this.formatFrom);
    const tempTo = this.datepipe.transform(to, this.formatTo);
    if (tempFrom != null && tempTo != null) {
      this.dates.from = tempFrom;
      this.dates.to = tempTo;
      this.sendDates.emit(this.dates);
    }
  }

  autoApplyFn() {
    if (this.autoApply && this.dateForm.controls.toDate.value != null) {
      this.setDates(null);
    }
  }

  setDates(event: any) {
    let initDate = this.dateForm.controls.fromDate.value ?? '';
    let finalDate = this.dateForm.controls.toDate.value ?? '';

    if (initDate != '' && initDate.toString() == finalDate.toString()) {
      this.dates.from = initDate;
      this.dates.to = finalDate.setHours(23, 59, 59);
      this.sendDates.emit(this.dates);
      this.selectedOption = '';
    }

    if (initDate == '' && finalDate == '') {
      this.dates.from = initDate;
      this.dates.to = finalDate;
      this.sendDates.emit(this.dates);
      this.selectedOption = '';
    }

    if (initDate != '' && finalDate == '') {
      finalDate = this.datepipe.transform(Date.now(), this.formatTo);
      initDate = this.datepipe.transform(initDate, this.formatFrom);
      this.dates.from = initDate;
      this.dates.to = finalDate;
      this.sendDates.emit(this.dates);
      this.selectedOption = '';
    }

    if (initDate == '' && finalDate != '') {
      finalDate = this.datepipe.transform(finalDate, this.formatTo);
      let tempFinalDate = new Date(finalDate);
      let tempDate = subDays(tempFinalDate, 30);
      initDate = this.datepipe.transform(tempDate, this.formatFrom);
      this.dates.from = initDate;
      this.dates.to = finalDate;
      this.sendDates.emit(this.dates);
      this.selectedOption = '';
    }

    if (initDate != '' && finalDate != '') {
      if (finalDate < initDate) {
        return this.toastr.error(
          'Error',
          'La fecha inicial no puede ser posterior a la fecha final.'
        );
      } else {
        finalDate = this.datepipe.transform(finalDate, this.formatTo);
        initDate = this.datepipe.transform(initDate, this.formatFrom);
        this.dates.from = initDate;
        this.dates.to = finalDate;
        this.sendDates.emit(this.dates);
        this.selectedOption = '';
      }
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    const target = event.target as HTMLElement;

    // Comprobar si el clic ocurrió fuera del componente y no en el área del botón
    if (
      this.visibleAtClickStart &&
      !this.elementRef.nativeElement.contains(target) &&
      !target.closest('.filter-icon') &&
      !target.closest('.mat-calendar-table') &&
      !target.closest('.mat-calendar') &&
      !target.closest('.mat-date-range-picker')
    ) {
      this.clickOutside.emit();
    }
  }
}
